import React, { useState } from 'react';
import { Search, User, Menu, X, Home, Calendar, Ticket, HelpCircle, Moon, Sun } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { SearchDropdown } from './SearchDropdown';

interface HeaderProps {
  darkMode: boolean;
  toggleDarkMode: () => void;
  currentPage: string;
  onNavigate: (page: string) => void;
  isLoggedIn?: boolean;
}

export function Header({ darkMode, toggleDarkMode, currentPage, onNavigate, isLoggedIn = false }: HeaderProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const menuItems = [
    { icon: Home, label: 'Home', page: 'home' },
    { icon: Calendar, label: 'Criar Eventos', page: 'produtor' },
    { icon: Ticket, label: 'Meus Ingressos', page: 'my-tickets' },
    { icon: HelpCircle, label: 'Ajuda', page: 'ajuda' },
  ];

  const handleNavigation = (page: string) => {
    onNavigate(page);
    setIsSearchOpen(false);
    setSearchQuery('');
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    // Abrir dropdown quando há texto ou fechar quando está vazio
    if (value.trim()) {
      setIsSearchOpen(true);
    } else {
      setIsSearchOpen(false);
    }
  };

  const handleSearchFocus = () => {
    // Sempre abrir o dropdown quando o campo ganha foco
    setIsSearchOpen(true);
  };

  const handleSearchClose = () => {
    setIsSearchOpen(false);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setIsSearchOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 h-18 glassmorphism border-b border-border/20 bg-background/80 backdrop-blur-md">
      <div className="container mx-auto px-4 h-full flex items-center justify-between">
        {/* Logo */}
        <div className="flex-shrink-0 mr-8">
          <button 
            onClick={() => handleNavigation('home')}
            className="group flex items-center space-x-3 transition-all duration-300 ease-out hover:scale-105 hover:opacity-90 hover:drop-shadow-lg"
          >
            <img 
              src="https://app.uniqueingressos.com.br/images/unique-logo.svg" 
              alt="Unique Ingressos" 
              className={`h-8 w-auto transition-all duration-300 group-hover:scale-105 ${
                darkMode 
                  ? 'filter brightness-0 invert' 
                  : 'filter brightness-0'
              }`}
            />
          </button>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center space-x-6">
          {menuItems.map((item) => (
            <button
              key={item.label}
              onClick={() => handleNavigation(item.page)}
              className={`group relative flex items-center space-x-2 py-2 px-3 rounded-lg transition-all duration-300 ease-out transform hover:scale-105 hover:-translate-y-0.5 ${
                currentPage === item.page 
                  ? 'text-orange-primary bg-orange-primary/10 border border-orange-primary/20 shadow-sm' 
                  : 'text-foreground/80 hover:text-orange-primary hover:bg-orange-primary/5 hover:shadow-md'
              }`}
            >
              {/* Hover glow effect */}
              <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-orange-primary/20 to-orange-primary/10 opacity-0 group-hover:opacity-100 transition-all duration-300 ease-out blur-sm scale-110" />
              
              {/* Icon with enhanced hover */}
              <item.icon 
                size={20} 
                className="relative z-10 transition-all duration-300 ease-out group-hover:scale-110 group-hover:drop-shadow-sm" 
              />
              
              {/* Text with enhanced hover */}
              <span className="relative z-10 text-sm transition-all duration-300 ease-out group-hover:font-medium">
                {item.label}
              </span>
              
              {/* Subtle underline animation */}
              <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-0 h-0.5 bg-orange-primary group-hover:w-2/3 transition-all duration-300 ease-out" />
            </button>
          ))}
        </nav>

        {/* Search Bar - Hidden on mobile, visible on tablet+ */}
        <div className="hidden md:flex items-center flex-1 max-w-md mx-6 relative">
          <div className="search-input-container">
            <Input
              type="text"
              placeholder="Buscar eventos, cidades..."
              value={searchQuery}
              onChange={handleSearchInputChange}
              onFocus={handleSearchFocus}
              className={`search-input-with-icon w-full border-border/30 glassmorphism-light transition-all duration-300 ease-out focus:scale-[1.02] focus:shadow-lg ${
                darkMode 
                  ? 'bg-white/10 text-white placeholder:text-white/60 focus:bg-white/15' 
                  : 'bg-black/5 text-foreground placeholder:text-muted-foreground focus:bg-black/10'
              }`}
            />
            {searchQuery ? (
              <div className="search-icon-container interactive group" onClick={clearSearch}>
                <X className={`search-icon transition-all duration-300 ease-out group-hover:scale-110 group-hover:rotate-90 ${
                  darkMode 
                    ? 'text-white/60 hover:text-white group-hover:drop-shadow-sm' 
                    : 'text-muted-foreground hover:text-foreground group-hover:drop-shadow-sm'
                }`} />
              </div>
            ) : (
              <div className="search-icon-container">
                <Search className={`search-icon ${darkMode ? 'text-white/60' : 'text-muted-foreground'}`} />
              </div>
            )}
          </div>

          {/* Search Dropdown */}
          <SearchDropdown
            query={searchQuery}
            isOpen={isSearchOpen}
            onClose={handleSearchClose}
          />
        </div>

        {/* Right Side - Only Dark Mode Toggle */}
        <div className="flex items-center space-x-4">
          {/* Dark Mode Toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleDarkMode}
            className={`group relative transition-all duration-300 ease-out transform hover:scale-110 hover:-translate-y-0.5 hover:shadow-lg rounded-xl ${
              darkMode 
                ? 'text-white hover:text-orange-primary hover:bg-white/15' 
                : 'text-foreground hover:text-orange-primary hover:bg-black/10'
            }`}
          >
            {/* Hover glow effect */}
            <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-orange-primary/20 to-orange-primary/10 opacity-0 group-hover:opacity-100 transition-all duration-300 ease-out blur-sm scale-110" />
            
            {/* Icon with rotation animation */}
            <div className="relative z-10 transition-all duration-500 ease-out group-hover:rotate-180">
              {darkMode ? <Sun size={20} /> : <Moon size={20} />}
            </div>
          </Button>

          {/* User Profile - Hidden on mobile */}
          <div className="hidden sm:block">
            {/* Placeholder for user profile when needed */}
          </div>
        </div>
      </div>
    </header>
  );
}